import { route, string, union } from "react-router-typesafe-routes";
import { zod } from "react-router-typesafe-routes/zod";
import { z } from "zod/v4";

/**
 * V2 route tree, served at the application root. (Legacy `/v2/*` URLs are
 * redirected to root in `AppRoutes`.) Features not yet migrated serve the V1
 * page at the same path (see `V2Routes`); the URL stays stable across the swap.
 */
export const v2Route = route({
  path: "*",
  children: {
    home: route({ path: "" }),
    entities: route({
      path: "entities/*",
      searchParams: {
        page: zod(z.number().optional()),
        pageSize: zod(z.number().int().min(1).max(100).optional()),
        sortOrder: union(["name", "nameJa", "date", "updatedAt"]),
        sortOrderDirection: union(["asc", "desc"]),
        idOrName: zod(z.string().optional()),
        categoryId: zod(z.string().optional()),
        referenceId: zod(z.string().optional()),
        excludeDeactivated: zod(z.boolean().optional()),
        gdtfFrom: zod(z.string().optional()),
        gdtfTo: zod(z.string().optional()),
        geoItemId: zod(z.string().optional()),
        selectedEntity: zod(z.string().uuid().optional()),
        "tab-entityDetail": union(["data", "changeRequests", "lineage"]),
        view: union(["table", "timeline", "map"]),
        tlCenterYear: zod(z.number().optional()),
        tlScale: zod(z.number().optional()),
        mapYearMin: zod(z.number().optional()),
        mapYearMax: zod(z.number().optional()),
        mapLat: zod(z.number().optional()),
        mapLng: zod(z.number().optional()),
        mapZoom: zod(z.number().optional()),
      },
      children: {
        detail: route({ path: ":id", params: { id: string() } }),
      },
    }),
    relations: route({
      path: "relations/*",
      searchParams: {
        page: zod(z.number().optional()),
        pageSize: zod(z.number().optional()),
        sortOrder: union(["name", "nameJa", "date", "updatedAt"]),
        sortOrderDirection: union(["asc", "desc"]),
        idOrName: zod(z.string().optional()),
        categoryId: zod(z.string().optional()),
        referenceId: zod(z.string().optional()),
        excludeDeactivated: zod(z.boolean().optional()),
        gdtfFrom: zod(z.string().optional()),
        gdtfTo: zod(z.string().optional()),
        selectedRelation: zod(z.string().uuid().optional()),
        geoItemId: zod(z.string().optional()),
        "tab-relationDetail": union(["data", "changeRequests", "lineage"]),
        view: union(["table", "timeline"]),
        tlCenterYear: zod(z.number().optional()),
        tlScale: zod(z.number().optional()),
      },
      children: {
        detail: route({ path: ":id" }),
      },
    }),
    compoundEntities: route({
      path: "compound-entities/*",
      searchParams: {
        page: zod(z.number().optional()),
        pageSize: zod(z.number().optional()),
        view: union(["table", "timeline"]),
        version: zod(z.string().optional()),
        gdtfFrom: zod(z.string().optional()),
        gdtfTo: zod(z.string().optional()),
        tlCenterYear: zod(z.number().optional()),
        tlScale: zod(z.number().optional()),
        selectedCompoundEntity: zod(z.string().uuid().optional()),
      },
      children: {
        detail: route({ path: ":id", params: { id: string() } }),
      },
    }),
    compoundRecordComposers: route({
      path: "compound-record-composers/*",
      searchParams: {
        page: zod(z.number().optional()),
        pageSize: zod(z.number().optional()),
        selectedComposer: zod(z.string().uuid().optional()),
      },
      children: {
        detail: route({ path: ":id", params: { id: string() } }),
      },
    }),
    compoundRecordVersions: route({
      path: "compound-record-versions/*",
      searchParams: {
        page: zod(z.number().optional()),
        pageSize: zod(z.number().optional()),
      },
    }),
    evidences: route({
      path: "evidences/*",
      searchParams: {
        page: zod(z.number().optional()),
        pageSize: zod(z.number().optional()),
        sortOrderDirection: union(["asc", "desc"]),
        referenceId: zod(z.string().optional()),
        userId: zod(z.string().optional()),
        selectedEvidence: zod(z.string().uuid().optional()),
        "tab-evidenceDetail": union(["data", "lineage"]),
        selectedEvidenceVersion: zod(z.string().uuid().optional()),
      },
      children: {
        detail: route({ path: ":id", params: { id: string() } }),
      },
    }),
    references: route({
      path: "references/*",
      searchParams: {
        selectedReference: zod(z.string().uuid().optional()),
      },
      children: {
        detail: route({ path: ":id", params: { id: string() } }),
      },
    }),
    // Standalone popup window for the reference-file image viewer (mounted outside V2Layout).
    referenceFiles: route({
      path: "reference-files/*",
      children: {
        slides: route({
          path: ":referenceId/:referenceFileId",
          params: { referenceId: string(), referenceFileId: string() },
          searchParams: {
            page: zod(z.number().optional()),
            folio: zod(z.number().optional()),
          },
        }),
      },
    }),
    georeferencer: route({ path: "georeferencer/*" }),
    geoItems: route({
      path: "geo-items/*",
      searchParams: {
        page: zod(z.number().int().min(0).optional()),
        pageSize: zod(z.number().int().min(1).max(100).optional()),
        label: zod(z.string().optional()),
      },
      children: {
        detail: route({ path: ":id", params: { id: string() } }),
      },
    }),
    areaInput: route({
      path: "area-input/*",
      children: {
        // Standalone GeoItem creation — still the V1 page, served within the V2 shell.
        create: route({ path: "create" }),
        record: route({
          path: ":recordId",
          children: {
            detail: route({
              path: ":geoItemId",
              params: { geoItemId: string() },
              children: {
                edit: route({ path: "edit" }),
              },
            }),
            create: route({
              path: "create",
              searchParams: {
                duplicateGeoItemId: zod(z.string().uuid().optional()),
                h3IndexesFileURLs: zod(z.array(z.string().url()).optional()),
              },
            }),
            createWithOverlayImg: route({
              path: "create-with-overlay",
            }),
          },
        }),
      },
    }),
    dataImportProjects: route({
      path: "data-import-projects/*",
      children: {
        list: route({
          path: "",
          searchParams: {
            page: zod(z.number().optional()),
            pageSize: zod(z.number().optional()),
            search: zod(z.string().optional()),
            excludeDeactivated: zod(z.boolean().optional()),
            seq: zod(z.string().optional()),
            name: zod(z.string().optional()),
            status: zod(z.string().optional()),
            references: zod(z.string().optional()),
            startAtFrom: zod(z.string().optional()),
            startAtTo: zod(z.string().optional()),
            endAtFrom: zod(z.string().optional()),
            endAtTo: zod(z.string().optional()),
          },
        }),
        detail: route({
          path: ":id",
          searchParams: {
            // Deep-link a node peek open on the pipeline (e.g. from the entity lineage edge click).
            peekType: union([
              "dataSource",
              "evidenceSet",
              "batchEvidencesJob",
              "batchChangeRequestsJob",
              "evidenceDrafts",
              "records",
              "entityComposer",
              "relationComposer",
              "recordUpdateComposer",
              "evidenceImporter",
            ]),
            peekId: zod(z.string().uuid().optional()),
            // Restores the active tab of a tabbed peek (e.g. the BCR job change-requests tab).
            peekTab: union(["info", "changeRequests", "evidences"]),
          },
          children: {
            dataSourceDesigner: route({ path: "data-sources/designer" }),
            dataSource: route({ path: "data-sources/:dataSourceId", params: { dataSourceId: string() } }),
            evidenceSet: route({ path: "evidence-sets/:evidenceSetId", params: { evidenceSetId: string() } }),
            batchEvidencesJob: route({ path: "batch-evidences-jobs/:jobId", params: { jobId: string() } }),
            batchChangeRequestsJob: route({ path: "batch-change-request-jobs/:jobId", params: { jobId: string() } }),
            pipelineJobRun: route({
              path: "data-import-pipeline-jobs/runs/:jobRunId",
              params: { jobRunId: string() },
            }),
          },
        }),
      },
    }),
    changeRequests: route({
      path: "change-requests/*",
      searchParams: {
        page: zod(z.number().optional()),
        pageSize: zod(z.number().optional()),
        sortOrder: union(["createdAt", "updatedAt", "status", "comments"]),
        sortOrderDirection: union(["asc", "desc"]),
        search: zod(z.string().optional()),
        status: zod(z.string().optional()),
        modelType: zod(z.string().optional()),
        hasReviewComments: zod(z.boolean().optional()),
        categoryId: zod(z.string().optional()),
        dataImportProjectId: zod(z.string().optional()),
        recordComposerDefinitionId: zod(z.string().optional()),
        batchChangeRequestsJobId: zod(z.string().optional()),
        batchChangeRequestsJobRunId: zod(z.string().optional()),
        selectedChangeRequest: zod(z.string().uuid().optional()),
      },
      children: {
        detail: route({ path: ":id", params: { id: string() } }),
      },
    }),
    dbpjkitWorkerJobs: route({
      path: "dbpjkit-worker-jobs/*",
      searchParams: {
        page: zod(z.number().optional()),
        pageSize: zod(z.number().optional()),
        sortOrder: union(["commandName", "googleSpreadsheetId", "googleSheetId", "createdAt", "updatedAt"]),
        sortOrderDirection: union(["asc", "desc"]),
        commandName: zod(z.string().optional()),
        spreadsheetId: zod(z.string().optional()),
        selectedDbpjkitWorkerJob: zod(z.string().uuid().optional()),
      },
      children: {
        detail: route({ path: ":id", params: { id: string() } }),
      },
    }),
    dataSourceTemplates: route({
      path: "data-source-templates/*",
      searchParams: {
        page: zod(z.number().optional()),
        pageSize: zod(z.number().optional()),
        search: zod(z.string().optional()),
        excludeDeactivated: zod(z.boolean().optional()),
        selectedDataSourceTemplate: zod(z.string().uuid().optional()),
      },
      children: {
        detail: route({ path: ":id", params: { id: string() } }),
      },
    }),
    admin: route({
      path: "admin",
      children: {
        organizations: route({
          path: "organizations/*",
          searchParams: {
            page: zod(z.number().optional()),
            pageSize: zod(z.number().optional()),
            search: zod(z.string().optional()),
          },
        }),
        tenants: route({
          path: "tenants/*",
          searchParams: {
            page: zod(z.number().optional()),
            pageSize: zod(z.number().optional()),
            search: zod(z.string().optional()),
            selectedTenant: zod(z.string().uuid().optional()),
            userIds: zod(z.array(z.string().uuid()).optional()),
          },
          children: {
            detail: route({ path: ":id", params: { id: string() } }),
          },
        }),
        categories: route({
          path: "categories/*",
          searchParams: {
            "tab-categoriesPage": union([
              "categories",
              "entityCategoriesTree",
              "relationCategoriesTree",
              "entityCategoriesTable",
              "relationCategoriesTable",
            ]),
            page: zod(z.number().optional()),
            pageSize: zod(z.number().optional()),
            search: zod(z.string().optional()),
            typeId: union(["entity", "relation"]),
            excludeDeactivated: zod(z.boolean().optional()),
            selectedCategory: zod(z.string().uuid().optional()),
          },
          children: {
            detail: route({ path: ":id", params: { id: string() } }),
          },
        }),
        recordMetadataSchema: route({
          path: "record-metadata-schema/*",
          searchParams: {
            page: zod(z.number().optional()),
            pageSize: zod(z.number().optional()),
            search: zod(z.string().optional()),
            selectedRecordMetadataSchema: zod(z.string().uuid().optional()),
          },
          children: {
            detail: route({ path: ":id", params: { id: string() } }),
          },
        }),
        relationTypes: route({
          path: "relation-types/*",
          searchParams: {
            page: zod(z.number().optional()),
            pageSize: zod(z.number().optional()),
            search: zod(z.string().optional()),
            excludeDeactivated: zod(z.boolean().optional()),
            selectedRelationType: zod(z.string().uuid().optional()),
          },
          children: {
            list: route({ path: "" }),
            detail: route({ path: ":id" }),
          },
        }),
        products: route({
          path: "products/*",
          searchParams: {
            page: zod(z.number().optional()),
            pageSize: zod(z.number().optional()),
            search: zod(z.string().optional()),
            selectedProduct: zod(z.string().uuid().optional()),
          },
          children: {
            detail: route({ path: ":id" }),
          },
        }),
        users: route({
          path: "users/*",
          searchParams: {
            page: zod(z.number().optional()),
            pageSize: zod(z.number().optional()),
            selectedUser: zod(z.string().uuid().optional()),
          },
          children: {
            detail: route({ path: ":id", params: { id: string() } }),
          },
        }),
        groups: route({
          path: "groups/*",
          searchParams: {
            page: zod(z.number().optional()),
            pageSize: zod(z.number().optional()),
            selectedGroup: zod(z.string().uuid().optional()),
          },
          children: {
            detail: route({ path: ":id", params: { id: string() } }),
          },
        }),
      },
    }),
  },
});
